import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Play } from "lucide-react";

const RULES = [
  "Have a natural, real conversation first — like texting someone you genuinely like",
  "Never push sales or money early; mention paid content only when he clearly shows interest",
  "Flirty, playful and completely open — zero inhibitions, no topic is off limits",
  "Adapt to his form of communication: match his slang, message length, and overall tone",
  "Mirror his pace: short replies to short messages, don't interrogate him with questions",
  "For lonely men: patience and emotional connection always come before anything else",
  "Use emojis naturally but sparingly: 💜🥰😏😘✨",
  "Respond in the same language the fan uses",
  "Keep messages feeling personal and real — never scripted or salesy",
  "Make him feel like the most desired man in the world",
];

const DEFAULT_RULES_TEXT = RULES.map((r) => "• " + r).join("\n");

export default function AISettings() {
  const { t } = useT();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testMsg, setTestMsg] = useState("hey beautiful, what are you up to tonight?");
  const [testNsfw, setTestNsfw] = useState(false);
  const [testReply, setTestReply] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/ai/settings").then((r) => setSettings(r.data));
  }, []);

  // Okamžité uloženie jedného poľa (prepínače) — bez čakania na Uložiť
  const saveField = async (patch) => {
    setSettings((p) => ({ ...p, ...patch }));
    try {
      const r = await api.put("/ai/settings", patch);
      setSettings(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Uloženie zlyhalo");
      const r2 = await api.get("/ai/settings").catch(() => null);
      if (r2) setSettings(r2.data);
    }
  };

  const save = async () => {
    setSaving(true);
    // Číslo s podporou nuly (0 je platná hodnota — napr. bedtime vypnutý!)
    const num = (v, dflt) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : dflt;
    };
    const payload = {
      custom_persona: settings.custom_persona,
      persona_rules: settings.persona_rules ?? null,
      auto_reply: !!settings.auto_reply,
      nsfw_spend_threshold: num(settings.nsfw_spend_threshold, 0),
      default_language: settings.default_language,
      session_max_minutes: num(settings.session_max_minutes, 60),
      bedtime_cooldown_hours: num(settings.bedtime_cooldown_hours, 8),
      bedtime_message: settings.bedtime_message || null,
      realistic_typing: !!settings.realistic_typing,
      typing_speed_cps: num(settings.typing_speed_cps, 5.0),
      min_send_delay_seconds: num(settings.min_send_delay_seconds, 5),
      max_send_delay_seconds: num(settings.max_send_delay_seconds, 120),
      llm_model: settings.llm_model || null,
      // ── PPV / hot mód (chýbali → zmeny sa zahadzovali) ──
      ppv_enabled: !!settings.ppv_enabled,
      ppv_cooldown_minutes: num(settings.ppv_cooldown_minutes, 45),
      ppv_max_per_day: num(settings.ppv_max_per_day, 3),
      ppv_base_price: num(settings.ppv_base_price, 10),
      ppv_max_price: num(settings.ppv_max_price, 60),
      ppv_free_max_per_day: num(settings.ppv_free_max_per_day, 1),
      ppv_min_messages_first: num(settings.ppv_min_messages_first, 6),
      ppv_hot_after_messages: num(settings.ppv_hot_after_messages, 3),
      ppv_haggle_max_drops: num(settings.ppv_haggle_max_drops, 2),
      ppv_haggle_min_price: num(settings.ppv_haggle_min_price, 5),
      followup_enabled: settings.followup_enabled ?? true,
      followup_after_days: num(settings.followup_after_days, 3),
      persona_timezone: settings.persona_timezone ?? "Europe/Bratislava",
      typos_enabled: settings.typos_enabled ?? true,
      ppv_style: settings.ppv_style ?? "balanced",
      funnel_url: (settings.funnel_url ?? "").trim() || null,
      funnel_enabled: settings.funnel_enabled ?? true,
      funnel_hot_threshold: num(settings.funnel_hot_threshold, 3),
      funnel_max_total: num(settings.funnel_max_total, 3),
    };
    try {
      const r = await api.put("/ai/settings", payload);
      const saved = r?.data ?? {};
      // Over, že DB naozaj uložila to, čo sme poslali (odhalí chýbajúci stĺpec / RLS)
      const bad = Object.keys(payload).filter((k) => {
        const a = payload[k], b = saved[k];
        if (a === null || a === undefined) return false;
        if (typeof a === "boolean") return !!b !== a;
        if (typeof a === "number") return Number(b) !== a;
        return String(b ?? "") !== String(a);
      });
      setSettings(saved);
      if (bad.length) {
        toast.error(`DB neuložila: ${bad.join(", ")} — chýbajú stĺpce. Spusti SQL migrácie (dash-check.sql).`,
          { duration: 12000 });
      } else {
        toast.success(t("saved"));
      }
    } catch (e) {
      toast.error(`Uloženie zlyhalo: ${e?.response?.data?.detail || e?.message || "?"}`, { duration: 10000 });
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    setTesting(true);
    setTestReply(null);
    try {
      const r = await api.post("/ai/test", {
        message: testMsg,
        nsfw: testNsfw,
        spend: testNsfw ? 50 : 0,
      });
      setTestReply(r.data.reply);
    } catch (e) {
      toast.error("AI test failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setTesting(false);
    }
  };

  if (!settings) return <div className="p-12 font-serif text-2xl text-white/40">loading…</div>;

  return (
    <div className="p-8 lg:p-12 max-w-5xl fade-up">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.35em] text-white/40">{t("ai_settings")}</div>
        <h1 className="font-serif text-5xl mt-2">
          The voice of <span className="italic text-fuchsia-400">her</span>.
        </h1>
        <p className="text-sm text-white/50 mt-3 max-w-xl">
          Fine-tune the AI persona. She auto-detects language, adapts tone, and escalates from SFW to NSFW based on spend.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: settings */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <div className="flex items-center justify-between mb-4">
              <div>
                <Label className="text-base font-serif">
                  Globálny AI vypínač
                </Label>
                <div className="text-xs text-white/40 mt-0.5 max-w-md">
                  Keď je <b className="text-rose-400">VYPNUTÝ</b> → AI nikomu neodpovedá (override pre všetkých fanov, ignoruje per-fan toggle).
                  Keď je <b className="text-emerald-400">ZAPNUTÝ</b> → AI odpovedá tým fanom čo majú per-fan AI tiež zapnuté.
                </div>
              </div>
              <Switch
                checked={settings.auto_reply}
                onCheckedChange={(v) => saveField({ auto_reply: v })}
                data-testid="switch-auto-reply"
              />
            </div>
            <div className={`text-[10px] uppercase tracking-widest mt-2 ${settings.auto_reply ? "text-emerald-400" : "text-rose-400"}`}>
              {settings.auto_reply ? "● AI je AKTÍVNE pre všetkých zapnutých fanov" : "○ AI je VYPNUTÉ pre všetkých fanov"}
            </div>
          </Card>

          <Card>
            <Label className="text-base font-serif">{t("nsfw_threshold")}</Label>
            <div className="text-xs text-white/40 mt-0.5 mb-3">Auto-unlock NSFW persona when fan's total spend crosses this.</div>
            <Input
              type="number"
              value={settings.nsfw_spend_threshold}
              onChange={(e) => setSettings({ ...settings, nsfw_spend_threshold: e.target.value })}
              className="bg-transparent border-white/10 rounded-none w-32"
              data-testid="input-nsfw-threshold"
            />
          </Card>

          <Card>
            <Label className="text-base font-serif">⌨️ Realistické písanie</Label>
            <div className="text-xs text-white/40 mt-1 mb-4 max-w-md">
              AI nepošle odpoveď okamžite — počká podľa <b>rýchlosti písania</b> a dĺžky správy,
              aby to vyzeralo prirodzene. Krátka správa ~5s, dlhá ~30-60s.
            </div>
            <div className="flex items-center justify-between mb-4">
              <Label>Zapnúť realistic typing</Label>
              <Switch
                checked={settings.realistic_typing !== false}
                onCheckedChange={(v) => saveField({ realistic_typing: v })}
                data-testid="switch-realistic-typing"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/40">Rýchlosť (znakov/s)</Label>
                <Input
                  type="number" step="0.5" min="1"
                  value={settings.typing_speed_cps ?? 5}
                  onChange={(e) => setSettings({ ...settings, typing_speed_cps: parseFloat(e.target.value) || 5 })}
                  className="bg-transparent border-white/10 rounded-none mt-1"
                  data-testid="input-typing-cps"
                />
                <div className="text-[10px] text-white/30 mt-1">5 = priemer · 3 = pomalé · 8 = rýchle</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/40">Min. delay (s)</Label>
                <Input
                  type="number" min="0"
                  value={settings.min_send_delay_seconds ?? 5}
                  onChange={(e) => setSettings({ ...settings, min_send_delay_seconds: parseInt(e.target.value) || 0 })}
                  className="bg-transparent border-white/10 rounded-none mt-1"
                  data-testid="input-min-delay"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/40">Max. delay (s)</Label>
                <Input
                  type="number" min="5"
                  value={settings.max_send_delay_seconds ?? 120}
                  onChange={(e) => setSettings({ ...settings, max_send_delay_seconds: parseInt(e.target.value) || 120 })}
                  className="bg-transparent border-white/10 rounded-none mt-1"
                  data-testid="input-max-delay"
                />
              </div>
            </div>
          </Card>

          <Card>
            <Label className="text-base font-serif">🤖 LLM model</Label>
            <div className="text-xs text-white/40 mt-1 mb-3 max-w-md">
              OpenRouter model. <b className="text-rose-300">Claude odmieta explicit NSFW.</b>{" "}
              Pre full uncensored chat zvoľ uncensored model:
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
              {[
                { v: "sao10k/l3.3-euryale-70b", label: "Euryale 70B", nsfw: "\u2705 full NSFW" },
                { v: "neversleep/llama-3.1-lumimaid-70b", label: "Lumimaid 70B", nsfw: "\u2705 full NSFW" },
                { v: "nothingiisreal/mn-celeste-12b", label: "Celeste 12B", nsfw: "\u2705 NSFW" },
              ].map((m) => (
                <button
                  key={m.v}
                  onClick={() => setSettings({ ...settings, llm_model: m.v || null })}
                  data-testid={`model-${m.v || "default"}`}
                  className={`text-left p-2.5 border text-xs ${
                    (settings.llm_model || "") === m.v
                      ? "border-fuchsia-500 bg-fuchsia-500/10"
                      : "border-white/10 hover:border-white/25"
                  }`}
                >
                  <div className="font-medium">{m.label}</div>
                  <div className="text-[10px] text-white/40 font-mono">{m.v || "OPENROUTER_MODEL env"}</div>
                  <div className="text-[10px] mt-1">{m.nsfw}</div>
                </button>
              ))}
            </div>
            <Input
              value={settings.llm_model || ""}
              onChange={(e) => setSettings({ ...settings, llm_model: e.target.value || null })}
              placeholder="alebo zadaj vlastné: provider/model-name"
              className="bg-transparent border-white/10 rounded-none font-mono text-xs"
              data-testid="input-custom-model"
            />
          </Card>

          <Card>
            <Label className="text-base font-serif">{t("default_language")}</Label>
            <div className="text-xs text-white/40 mt-0.5 mb-3">Auto = mirror the fan's language.</div>
            <Select
              value={settings.default_language}
              onValueChange={(v) => setSettings({ ...settings, default_language: v })}
            >
              <SelectTrigger className="bg-transparent border-white/10 rounded-none w-48" data-testid="select-lang">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[rgb(18,20,28)] border-white/10">
                <SelectItem value="auto">auto-detect</SelectItem>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="sk">Slovenčina</SelectItem>
                <SelectItem value="es">Español</SelectItem>
              </SelectContent>
            </Select>
          </Card>

          <Card>
            <Label className="text-base font-serif">😴 Bedtime / dĺžka konverzácie</Label>
            <div className="text-xs text-white/40 mt-1 mb-4 max-w-md">
              AI bude s fanúšikom písať maximálne <b>X minút</b>. Potom mu pošle „idem spať" správu
              a <b>Y hodín</b> nereaguje. Po cooldowne plynule nadviaže (kontext si pamätá).
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/40">Max dĺžka session (min)</Label>
                <Input
                  type="number"
                  value={settings.session_max_minutes ?? 60}
                  onChange={(e) => setSettings({ ...settings, session_max_minutes: parseInt(e.target.value) || 60 })}
                  className="bg-transparent border-white/10 rounded-none mt-1 w-32"
                  data-testid="input-session-minutes"
                  min="5"
                />
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-white/40">Cooldown po bedtime (h)</Label>
                <Input
                  type="number"
                  value={settings.bedtime_cooldown_hours ?? 8}
                  onChange={(e) => setSettings({ ...settings, bedtime_cooldown_hours: parseInt(e.target.value) || 8 })}
                  className="bg-transparent border-white/10 rounded-none mt-1 w-32"
                  data-testid="input-cooldown-hours"
                  min="1"
                />
              </div>
            </div>
            <div className="mt-4">
              <Label className="text-[10px] uppercase tracking-widest text-white/40">
                Bedtime správa (voliteľné — prázdne = AI vygeneruje v jazyku fana)
              </Label>
              <Textarea
                value={settings.bedtime_message || ""}
                onChange={(e) => setSettings({ ...settings, bedtime_message: e.target.value })}
                rows={2}
                placeholder='napr. "ok zlato, idem spinkať 😴 zajtra ti napíšem 💜"'
                className="bg-transparent border-white/10 rounded-none mt-1 text-sm"
                data-testid="textarea-bedtime"
              />
            </div>
          </Card>

      {/* ── AI PPV: automatické ponúkanie fotiek z Vaultu ── */}
      <Card className="p-6 space-y-4" data-testid="card-ppv">
        <div>
          <div className="font-serif text-xl text-white">💸 AI ponúkanie fotiek (PPV)</div>
          <p className="text-white/50 text-xs mt-1 max-w-2xl">
            AI sama vyhodnotí, kedy sa hodí ponúknuť platenú fotku, vyberie ju z Vaultu (len fotky
            označené 🤖 a nahrané vo Fanvue) a navrhne cenu podľa útraty fanúšika. Explicitné fotky
            ponúkne len keď je konverzácia zjavne sexuálna a fanúšik prekročil NSFW prah.
          </p>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm text-white/80">Zapnúť AI PPV ponuky</div>
          <Switch
            checked={!!settings.ppv_enabled}
            onCheckedChange={(v) => saveField({ ppv_enabled: v })}
            data-testid="switch-ppv"
          />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-white/40">🎚️ Štýl ponúkania — preset vyplní hodnoty nižšie</label>
            <div className="grid grid-cols-4 gap-2 mt-1">
              {[
                { v: "conservative", l: "Konzervatívny", d: "Zriedkavé ponuky. Po 2 odmietnutiach prestane núkať.",
                  vals: { ppv_cooldown_minutes: 90, ppv_max_per_day: 2, ppv_free_max_per_day: 1, ppv_min_messages_first: 10, ppv_hot_after_messages: 5, ppv_haggle_max_drops: 1 } },
                { v: "balanced", l: "Vyvážený", d: "Odporúčaný štart — ponuky v správnych chvíľach.",
                  vals: { ppv_cooldown_minutes: 45, ppv_max_per_day: 3, ppv_free_max_per_day: 1, ppv_min_messages_first: 6, ppv_hot_after_messages: 3, ppv_haggle_max_drops: 2 } },
                { v: "active", l: "Aktívny", d: "Pravidelné ponuky pre publikum zvyknuté kupovať.",
                  vals: { ppv_cooldown_minutes: 20, ppv_max_per_day: 5, ppv_free_max_per_day: 2, ppv_min_messages_first: 4, ppv_hot_after_messages: 2, ppv_haggle_max_drops: 2 } },
                { v: "custom", l: "Vlastné", d: "Nastav si hodnoty sám — nič sa neprepíše.", vals: null },
              ].map((o) => (
                <button key={o.v}
                  onClick={() => setSettings({ ...settings, ppv_style: o.v, ...(o.vals || {}) })}
                  data-testid={`ppvstyle-${o.v}`}
                  className={`text-left border p-3 transition-colors ${
                    (settings.ppv_style ?? "balanced") === o.v
                      ? "border-fuchsia-500 bg-fuchsia-500/10"
                      : "border-white/10 hover:border-white/25"
                  }`}>
                  <div className={`text-[11px] uppercase tracking-widest font-bold ${(settings.ppv_style ?? "balanced") === o.v ? "text-fuchsia-300" : "text-white/70"}`}>{o.l}</div>
                  <div className="text-[10px] text-white/40 mt-1 leading-snug">{o.d}</div>
                </button>
              ))}
            </div>
            <p className="text-white/30 text-[10px] mt-1">Klik na preset nastaví cooldown, limity, prah prvej fotky, hot mód aj vyjednávanie. Ručná zmena ľubovoľného poľa prepne na Vlastné.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">Cooldown medzi ponukami (min)</label>
            <input type="number" min="5" value={settings.ppv_cooldown_minutes ?? 45}
              onChange={(e) => setSettings({ ...settings, ppv_cooldown_minutes: Number(e.target.value), ppv_style: "custom" })}
              data-testid="input-ppv-cooldown"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">Max ponúk / fanúšik / deň</label>
            <input type="number" min="1" value={settings.ppv_max_per_day ?? 3}
              onChange={(e) => setSettings({ ...settings, ppv_max_per_day: Number(e.target.value), ppv_style: "custom" })}
              data-testid="input-ppv-maxday"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">Základná cena ($) — nový fanúšik</label>
            <input type="number" min="1" value={settings.ppv_base_price ?? 10}
              onChange={(e) => setSettings({ ...settings, ppv_base_price: Number(e.target.value), ppv_style: "custom" })}
              data-testid="input-ppv-base"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
          </div>
          <div className="md:col-span-2">
            <div className="flex items-baseline justify-between gap-3 flex-wrap">
              <label className="text-[10px] uppercase tracking-widest text-white/40">🔗 Odkaz na Fanvue / Linkdrop (Telegram funnel)</label>
              <a href="https://linkdrop.uk" target="_blank" rel="noopener noreferrer"
                data-testid="link-linkdrop"
                className="text-[10px] text-fuchsia-300 hover:text-fuchsia-200 underline underline-offset-2">
                Nemáš svoj Linkdrop? Klikni sem →
              </a>
            </div>
            <input value={settings.funnel_url ?? ""}
              onChange={(e) => setSettings({ ...settings, funnel_url: e.target.value })}
              placeholder="https://linkdrop.uk/tvoj-profil  alebo  https://fanvue.com/tvoj-profil"
              data-testid="input-funnel-url"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50 placeholder:text-white/25" />
            <p className="text-white/30 text-[10px] mt-1">
              Keď sa debata na Telegrame rozohreje, AI sama pozve fanúšika sem — jednou flirtujúcou vetou + odkazom.
              Prázdne pole = funnel vypnutý.
            </p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">🔗 Funnel: prah horúcich správ</label>
            <input type="number" min="1" max="10" value={settings.funnel_hot_threshold ?? 3}
              onChange={(e) => setSettings({ ...settings, funnel_hot_threshold: Number(e.target.value) })}
              data-testid="input-funnel-hot"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
            <p className="text-white/30 text-[10px] mt-1">Po koľkých horúcich správach pozve. Vyššie = zdržanlivejšie.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">🔗 Funnel: max pozvánok na fana</label>
            <input type="number" min="1" max="10" value={settings.funnel_max_total ?? 3}
              onChange={(e) => setSettings({ ...settings, funnel_max_total: Number(e.target.value) })}
              data-testid="input-funnel-max"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
            <p className="text-white/30 text-[10px] mt-1">Celkový strop. Medzi pozvánkami je pauza 48 h.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">🕐 Časové pásmo persony</label>
            <select value={settings.persona_timezone ?? "Europe/Bratislava"}
              onChange={(e) => setSettings({ ...settings, persona_timezone: e.target.value })}
              data-testid="select-timezone"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50">
              {["Europe/Bratislava","Europe/London","Europe/Berlin","Europe/Paris","Europe/Madrid",
                "America/New_York","America/Chicago","America/Los_Angeles","America/Sao_Paulo",
                "Asia/Dubai","Asia/Tokyo","Australia/Sydney"].map((tz) => (
                <option key={tz} value={tz} className="bg-black">{tz}</option>
              ))}
            </select>
            <p className="text-white/30 text-[10px] mt-1">AI žije v tomto čase — ráno káva, večer posteľ. Fanúšikom hovorí vždy SVOJ čas.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">💌 Follow-up: po koľkých dňoch ticha</label>
            <input type="number" min="1" value={settings.followup_after_days ?? 3}
              onChange={(e) => setSettings({ ...settings, followup_after_days: Number(e.target.value) })}
              data-testid="input-followup-days"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
            <p className="text-white/30 text-[10px] mt-1">AI sama oživí fanúšika, ktorý stíchol. Opakuje max raz za 7 dní.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">✍️ Občasné preklepy (3–5 %)</label>
            <button
              onClick={() => setSettings({ ...settings, typos_enabled: !(settings.typos_enabled ?? true) })}
              data-testid="toggle-typos"
              className={`w-full mt-1 px-3 py-2 text-sm border transition-colors ${
                (settings.typos_enabled ?? true)
                  ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                  : "border-white/10 text-white/40"
              }`}
            >{(settings.typos_enabled ?? true) ? "ZAPNUTÉ" : "VYPNUTÉ"}</button>
            <p className="text-white/30 text-[10px] mt-1">Občas preklep, niekedy s opravou „*slovo" — píše ako človek.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">💌 Follow-up zapnutý</label>
            <button
              onClick={() => setSettings({ ...settings, followup_enabled: !(settings.followup_enabled ?? true) })}
              data-testid="toggle-followup"
              className={`w-full mt-1 px-3 py-2 text-sm border transition-colors ${
                (settings.followup_enabled ?? true)
                  ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                  : "border-white/10 text-white/40"
              }`}
            >{(settings.followup_enabled ?? true) ? "ZAPNUTÝ" : "VYPNUTÝ"}</button>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">💬 Vyjednávanie: max znížení ceny</label>
            <input type="number" min="0" max="4" value={settings.ppv_haggle_max_drops ?? 2}
              onChange={(e) => setSettings({ ...settings, ppv_haggle_max_drops: Number(e.target.value), ppv_style: "custom" })}
              data-testid="input-haggle-drops"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
            <p className="text-white/30 text-[10px] mt-1">Keď fan namietne cenu, AI zľaví (−30% za krok). 0 = vypnuté.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">💬 Vyjednávanie: minimálna cena ($)</label>
            <input type="number" min="1" value={settings.ppv_haggle_min_price ?? 5}
              onChange={(e) => setSettings({ ...settings, ppv_haggle_min_price: Number(e.target.value), ppv_style: "custom" })}
              data-testid="input-haggle-min"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
            <p className="text-white/30 text-[10px] mt-1">Pod túto cenu AI nikdy nezľaví.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">🔥 Hot mód: ponúkni po X horúcich správach</label>
            <input type="number" min="1" value={settings.ppv_hot_after_messages ?? 3}
              onChange={(e) => setSettings({ ...settings, ppv_hot_after_messages: Number(e.target.value), ppv_style: "custom" })}
              data-testid="input-ppv-hot"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
            <p className="text-white/30 text-[10px] mt-1">Keď je debata sexuálne nabitá X správ po sebe, AI ponúkne fotku („do you wanna see it? 😏").</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">Prvá fotka najskôr po X správach</label>
            <input type="number" min="0" value={settings.ppv_min_messages_first ?? 6}
              onChange={(e) => setSettings({ ...settings, ppv_min_messages_first: Number(e.target.value), ppv_style: "custom" })}
              data-testid="input-ppv-minmsgs"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
            <p className="text-white/30 text-[10px] mt-1">Ak fanúšik o fotku sám požiada, pošle sa aj skôr.</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">Max FREE fotiek / fanúšik / deň</label>
            <input type="number" min="0" value={settings.ppv_free_max_per_day ?? 1}
              onChange={(e) => setSettings({ ...settings, ppv_free_max_per_day: Number(e.target.value), ppv_style: "custom" })}
              data-testid="input-ppv-freemax"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">Max cena ($) — strop</label>
            <input type="number" min="1" value={settings.ppv_max_price ?? 60}
              onChange={(e) => setSettings({ ...settings, ppv_max_price: Number(e.target.value), ppv_style: "custom" })}
              data-testid="input-ppv-max"
              className="w-full bg-black/40 border border-white/10 px-3 py-2 text-sm mt-1 outline-none focus:border-fuchsia-500/50" />
          </div>
        </div>
        <p className="text-white/30 text-[11px]">
          Cena rastie s útratou fanúšika (base + 15 % z doterajšej útraty, max strop). Nezabudni fotky vo Vaulte povoliť tlačidlom 🤖 a dať im popis — AI vyberá práve podľa neho. Fotku označenú 🎁 smie AI poslať aj zadarmo. Tú istú fotku nikdy nepošle tomu istému fanúšikovi dvakrát.
        </p>
      </Card>

          <Card>
            <Label className="text-base font-serif">{t("custom_persona")}</Label>
            <div className="text-xs text-white/40 mt-0.5 mb-3">Optional: override the default persona with your own voice.</div>
            <Textarea
              value={settings.custom_persona || ""}
              onChange={(e) => setSettings({ ...settings, custom_persona: e.target.value })}
              rows={6}
              placeholder='e.g. "You are Mia, 24, from Bratislava. Cheeky, playful, loves to tease..."'
              className="bg-transparent border-white/10 rounded-none"
              data-testid="textarea-persona"
            />
          </Card>

          <Button
            onClick={save}
            disabled={saving}
            data-testid="btn-save-ai"
            className="bg-white text-black hover:bg-white/90 rounded-none px-6 py-5 text-xs uppercase tracking-[0.25em]"
          >
            {saving ? "..." : t("save")}
          </Button>
        </div>

        {/* Right: rules + test */}
        <div className="space-y-6">
          <Card>
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={14} className="text-fuchsia-400" />
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300">{t("rules")}</div>
            </div>
            <Textarea
              value={settings.persona_rules ?? DEFAULT_RULES_TEXT}
              onChange={(e) => setSettings({ ...settings, persona_rules: e.target.value })}
              rows={10}
              data-testid="textarea-persona-rules"
              className="bg-black/40 border-white/10 text-xs leading-relaxed focus-visible:ring-fuchsia-500/40"
            />
            <div className="flex gap-2 mt-3">
              <button type="button" data-testid="btn-rules-default"
                onClick={() => saveField({ persona_rules: DEFAULT_RULES_TEXT })}
                className="text-[10px] uppercase tracking-widest px-3 py-1.5 border border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10 transition-colors">
                Predvolené
              </button>
              <button type="button" data-testid="btn-rules-save"
                onClick={() => saveField({ persona_rules: settings.persona_rules ?? DEFAULT_RULES_TEXT })}
                className="text-[10px] uppercase tracking-widest px-3 py-1.5 bg-fuchsia-500/20 text-fuchsia-200 hover:bg-fuchsia-500/30 transition-colors">
                Uložiť pravidlá
              </button>
            </div>
            <p className="text-white/30 text-[10px] mt-2">
              AI tieto pravidlá dodržiava pri každej odpovedi. „Predvolené" vráti pôvodný osvedčený zoznam.
            </p>
          </Card>

          <Card>
            <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-2">✅ AI MÔŽE</div>
            <ul className="space-y-1.5 text-xs text-white/65 leading-relaxed">
              <li>· Explicit sexual roleplay (18+ verified)</li>
              <li>· Sexting, dirty talk, detailný popis</li>
              <li>· Tease, escalate, monetize (PPV, tipy)</li>
              <li>· Diskutovať kinks fana (BDSM medzi dospelými)</li>
              <li>· Vulgárny jazyk ak ho fan preferuje</li>
            </ul>
            <div className="text-[10px] uppercase tracking-widest text-rose-300 mb-2 mt-4">⛔ AI NIKDY</div>
            <ul className="space-y-1.5 text-xs text-white/65 leading-relaxed">
              <li>· Maloletí / under-18 / school roleplay / baby voice</li>
              <li>· Non-consent / rape / force / drugging</li>
              <li>· Incest (vrátane „step" framingu)</li>
              <li>· Bestialita, snuff, extrémne harmful</li>
              <li>· Hate speech, slurs, vyhrážky</li>
              <li>· Deepfakes / impersonácia reálnych osôb</li>
              <li>· Doxxing / žiadanie reálnych údajov fana</li>
              <li>· Illegal services (drogy, zbrane, blackmail)</li>
            </ul>
            <div className="text-[10px] text-white/40 mt-3 border-t border-white/5 pt-2">
              Tieto pravidlá majú najvyššiu prioritu — žiadna persona/custom override ich nezruší.
              Chránia teba pred Fanvue banom.
            </div>
          </Card>

          <Card>
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-3">{t("test_ai")}</div>
            <Textarea
              value={testMsg}
              onChange={(e) => setTestMsg(e.target.value)}
              rows={3}
              className="bg-transparent border-white/10 rounded-none text-sm"
              data-testid="textarea-test-msg"
            />
            <div className="flex items-center gap-2 mt-3 mb-3">
              <Switch checked={testNsfw} onCheckedChange={setTestNsfw} data-testid="switch-test-nsfw" />
              <span className="text-xs text-white/60">NSFW mode</span>
            </div>
            <Button
              onClick={runTest}
              disabled={testing}
              data-testid="btn-run-test"
              className="w-full bg-fuchsia-500/20 border border-fuchsia-500/30 text-fuchsia-300 hover:bg-fuchsia-500/30 rounded-none text-xs uppercase tracking-widest"
            >
              <Play size={12} className="mr-2" />
              {testing ? "she's thinking…" : "run"}
            </Button>
            {testReply && (
              <div className="mt-3 border-l-2 border-fuchsia-500 bg-fuchsia-500/10 p-3 text-sm font-serif italic text-white/90" data-testid="test-reply">
                {testReply}
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ children }) {
  return <div className="bg-[rgb(18,20,28)] border border-white/5 p-5">{children}</div>;
}
